import React, { Component } from 'react';
import cn from 'classnames';
import Label from './Label';
import styles from './slider.module.scss';

interface IProps {
  ariaLabelledby?: string,
  ariaControls?: string,
  formatLabel?: (value?: number, type?: string) => string,
  maxValue?: number,
  minValue?: number,
  onSliderDrag: (event: MouseEvent | TouchEvent, key: string) => void,
  onSliderKeyDown: (event: React.KeyboardEvent, key: string) => void,
  percentage: number,
  type: string,
  value: number,
  showValueLabel?: boolean,
  disabled?: boolean,
}

class Slider extends Component<IProps> {
  // eslint-disable-next-line react/sort-comp
  private node: HTMLSpanElement | null;

  constructor(props: IProps) {
    super(props);

    this.node = null;

    this.handleMouseDown = this.handleMouseDown.bind(this);
    this.handleMouseUp = this.handleMouseUp.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleTouchStart = this.handleTouchStart.bind(this);
    this.handleTouchMove = this.handleTouchMove.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  componentWillUnmount() {
    this.removeDocumentMouseMoveListener();
    this.removeDocumentMouseUpListener();
    this.removeDocumentTouchEndListener();
    this.removeDocumentTouchMoveListener();
  }

  getStyle(): React.CSSProperties {
    const perc = (this.props.percentage || 0) * 100;

    return {
      position: 'absolute',
      left: `${ perc }%`,
    };
  }

  /**
   * Listen to mousemove event
   */
  addDocumentMouseMoveListener() {
    this.removeDocumentMouseMoveListener();
    this.node.ownerDocument.addEventListener('mousemove', this.handleMouseMove);
  }

  /**
   * Listen to mouseup event
   */
  addDocumentMouseUpListener() {
    this.removeDocumentMouseUpListener();
    this.node.ownerDocument.addEventListener('mouseup', this.handleMouseUp);
  }

  /**
   * Listen to touchmove event
   */
  addDocumentTouchMoveListener() {
    this.removeDocumentTouchMoveListener();
    this.node.ownerDocument.addEventListener('touchmove', this.handleTouchMove);
  }

  /**
   * Listen to touchend event
   */
  addDocumentTouchEndListener() {
    this.removeDocumentTouchEndListener();
    this.node.ownerDocument.addEventListener('touchend', this.handleTouchEnd);
  }

  removeDocumentMouseMoveListener() {
    this.node.ownerDocument.removeEventListener('mousemove', this.handleMouseMove);
  }

  removeDocumentMouseUpListener() {
    this.node.ownerDocument.removeEventListener('mouseup', this.handleMouseUp);
  }

  removeDocumentTouchMoveListener() {
    this.node.ownerDocument.removeEventListener('touchmove', this.handleTouchMove);
  }

  removeDocumentTouchEndListener() {
    this.node.ownerDocument.removeEventListener('touchend', this.handleTouchEnd);
  }

  handleMouseDown() {
    this.addDocumentMouseMoveListener();
    this.addDocumentMouseUpListener();
  }

  handleMouseUp() {
    this.removeDocumentMouseMoveListener();
    this.removeDocumentMouseUpListener();
  }

  handleMouseMove(event: MouseEvent | TouchEvent) {
    this.props.onSliderDrag(event, this.props.type);
  }

  handleTouchStart() {
    this.addDocumentTouchEndListener();
    this.addDocumentTouchMoveListener();
  }

  handleTouchMove(event: MouseEvent | TouchEvent) {
    this.props.onSliderDrag(event, this.props.type);
  }

  handleTouchEnd() {
    this.removeDocumentTouchMoveListener();
    this.removeDocumentTouchEndListener();
  }

  handleKeyDown(event: React.KeyboardEvent) {
    this.props.onSliderKeyDown(event, this.props.type);
  }

  render() {
    const style = this.getStyle();
    const sliderCls = cn(styles.slider, {
      [styles.isDisabled]: this.props.disabled,
    });
    return (
      <span
        className={ styles.sliderContainer }
        ref={ (node) => { this.node = node; } }
        style={ style }
      >
        { this.props.showValueLabel && (
          <Label
            formatLabel={ this.props.formatLabel }
            type="value"
          >
            { this.props.value }
          </Label>
        ) }
        <div
          aria-labelledby={ this.props.ariaLabelledby }
          aria-controls={ this.props.ariaControls }
          aria-valuemax={ this.props.maxValue }
          aria-valuemin={ this.props.minValue }
          aria-valuenow={ this.props.value }
          className={ sliderCls }
          draggable="false"
          onKeyDown={ this.handleKeyDown }
          onMouseDown={ this.handleMouseDown }
          onTouchStart={ this.handleTouchStart }
          role="slider"
          tabIndex={ 0 }
        />
      </span>
    );
  }
}

export default Slider;
